export interface GeoFix {
  lat: number;
  lng: number;
  accuracy: number;
  altitude: number | null;
  speed: number | null;
  heading: number | null;
  /** Epoch ms on the source's clock. */
  ts: number;
}

export type GeoErrorKind = 'denied' | 'unavailable' | 'timeout' | 'unsupported';

/**
 * Location provider abstraction. The PWA uses the browser Geolocation API; a
 * native shell can later provide its own implementation without touching the
 * TrackingEngine or the game engine.
 */
export interface GeoSource {
  readonly kind: 'browser' | 'replay' | 'native';
  start(onFix: (fix: GeoFix) => void, onError: (kind: GeoErrorKind, message: string) => void): void;
  stop(): void;
  /** Clock consistent with fix timestamps. */
  now(): number;
  /** Optional hook: the engine started recording a run. */
  onRecordingStarted?(): void;
}

export class BrowserGeoSource implements GeoSource {
  readonly kind = 'browser' as const;
  private watchId: number | null = null;

  start(onFix: (fix: GeoFix) => void, onError: (kind: GeoErrorKind, message: string) => void) {
    if (this.watchId !== null) return;
    if (!('geolocation' in navigator)) {
      onError('unsupported', 'This browser has no GPS support');
      return;
    }
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        // Some devices report stale or oddly-based timestamps; fall back to the wall clock.
        const ts = Math.abs(pos.timestamp - now) < 60_000 ? Math.round(pos.timestamp) : now;
        onFix({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          altitude: pos.coords.altitude,
          speed: pos.coords.speed,
          heading: pos.coords.heading,
          ts,
        });
      },
      (err) => {
        const kind: GeoErrorKind =
          err.code === err.PERMISSION_DENIED ? 'denied' : err.code === err.TIMEOUT ? 'timeout' : 'unavailable';
        onError(kind, err.message);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 30_000 },
    );
  }

  stop() {
    if (this.watchId !== null) navigator.geolocation.clearWatch(this.watchId);
    this.watchId = null;
  }

  now() {
    return Date.now();
  }
}

export async function geolocationPermission(): Promise<PermissionState | 'unknown'> {
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    return status?.state ?? 'unknown';
  } catch {
    return 'unknown';
  }
}
